import {create} from 'zustand';

const LS_KEY = 'ai_widget_thread_id';
const LS_CHART_THREADS_KEY = 'ai_widget_chart_thread_ids';

function loadPersistedThreadId() {
    try {
        return localStorage.getItem(LS_KEY) || null;
    } catch {
        return null;
    }
}

function saveThreadId(id) {
    try {
        if (id) localStorage.setItem(LS_KEY, id);
        else localStorage.removeItem(LS_KEY);
    } catch {
        /* ignore storage errors */
    }
}

function loadChartThreadIds() {
    try {
        const raw = localStorage.getItem(LS_CHART_THREADS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

function saveChartThreadIds(map) {
    try {
        localStorage.setItem(LS_CHART_THREADS_KEY, JSON.stringify(map || {}));
    } catch {
        /* ignore storage errors */
    }
}

const PANEL_WIDTH = 448;
const MAX_PANEL_WIDTH = 900;

/**
 * Global state for the CommunicationHub.
 *
 * aiContext — the reports the AI operates against.
 * activeEntityGroup — inline Team tab entity group thread (chart or product).
 * activeTab — which CommunicationHub tab is visible ('ai' | 'team').
 * panelWidth — current width of the communication panel (expand-only, min = PANEL_WIDTH).
 */
const useAiWidgetStore = create((set, get) => ({
    activeTab: 'ai',
    open: false,
    panelWidth: PANEL_WIDTH,
    panelResizeEndTick: 0,
    aiContext: null,
    activeEntityGroup: null,
    threadId: loadPersistedThreadId(),
    chartThreadIds: loadChartThreadIds(),

    setActiveTab: (activeTab) => set({activeTab}),
    setOpen: (open) => set({open}),
    setPanelWidth: (w) => set({panelWidth: Math.min(MAX_PANEL_WIDTH, Math.max(PANEL_WIDTH, w))}),
    tickPanelResizeEnd: () => set((s) => ({panelResizeEndTick: s.panelResizeEndTick + 1})),

    setAiContext: (context) => set({
        aiContext: context ? {
            documentType: context.documentType || 'report',
            documents: Array.isArray(context.documents)
                ? context.documents.map((d) => ({
                    documentId: d.documentId || null,
                    documentTitle: d.documentTitle || '',
                }))
                : [],
        } : null,
    }),

    clearAiContext: () => set({aiContext: null}),

    setActiveEntityGroup: (context) => set({
        activeEntityGroup: context ? {
            entityType: context.entityType || 'chart',
            entityId: context.entityId || null,
            entityTitle: context.entityTitle || '',
        } : null,
    }),

    clearActiveEntityGroup: () => set({activeEntityGroup: null}),

    /** @deprecated Use setActiveEntityGroup */
    setTeamContext: (context) => {
        get().setActiveEntityGroup(context);
    },

    /** @deprecated Use clearActiveEntityGroup */
    clearTeamContext: () => {
        get().clearActiveEntityGroup();
    },

    addDocumentToContext: ({documentId, documentTitle, documentType}) => {
        const {aiContext} = get();
        if (!documentId) return;
        if (aiContext && aiContext.documentType !== documentType) return;
        if (aiContext?.documents.some((d) => d.documentId === documentId)) return;
        set({
            aiContext: {
                documentType: documentType || 'report',
                documents: [
                    ...(aiContext?.documents || []),
                    {documentId, documentTitle: documentTitle || ''},
                ],
            },
        });
    },

    removeDocumentFromContext: (documentId) => {
        const {aiContext} = get();
        if (!aiContext) return;
        const remaining = aiContext.documents.filter((d) => d.documentId !== documentId);
        set({aiContext: remaining.length === 0 ? null : {...aiContext, documents: remaining}});
    },

    setThreadId: (threadId, chartId) => {
        const id = threadId || null;
        saveThreadId(id);
        const updates = {threadId: id};
        if (chartId && id) {
            const next = {...get().chartThreadIds, [chartId]: id};
            saveChartThreadIds(next);
            updates.chartThreadIds = next;
        }
        set(updates);
    },

    setChartThreadId: (chartId, threadId) => {
        if (!chartId) return;
        const next = {...get().chartThreadIds};
        if (threadId) {
            next[chartId] = threadId;
        } else {
            delete next[chartId];
        }
        saveChartThreadIds(next);
        set({chartThreadIds: next});
    },

    clearThreadId: () => {
        saveThreadId(null);
        set({threadId: null});
    },
}));

export default useAiWidgetStore;
